import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SAMPLE_DATA } from "./sampledata/apmcSampleData";
import AddLot from "./apmccomponents/AddLot";
import ViewLots from "./apmccomponents/ViewLots";
import FarmerDashboard from "./apmccomponents/FarmerDashboard";
import Grading from "./apmccomponents/Grading";
import LabTest from "./apmccomponents/LabTest";
import Auction from "./apmccomponents/Auction";
import Payment from "./apmccomponents/Payment";
import Logistics from "./apmccomponents/Logistics";

const COMPONENT_MAP = {
  "Add Lot": AddLot,
  "View Lots": ViewLots,
  "Farmer Dashboard": FarmerDashboard,
  "Grading": Grading,
  "Lab Test": LabTest,
  "Quality Reports": Grading,
  "Start Auction": Auction,
  "Live Bidding": Auction,
  "Bid History": Auction,
  "Weighing": ViewLots,
  "Generate Slip": ViewLots,
  "Final Price": Auction,
  "Pay Farmer": Payment,
  "Transaction Logs": Payment,
  "Digital Payments": Payment,
  "Dispatch": Logistics,
  "Track Delivery": Logistics,
  "Warehouse": Logistics,
};

const STAKEHOLDER_GROUPS = [
  {
    group: "Farmers",
    icon: "🧑‍🌾",
    options: ["Small Farmers", "FPOs", "Large Agribusiness"],
  },
  {
    group: "Commission Agents",
    icon: "🧑‍💼",
    options: ["Arthiyas", "Credit Providers", "Auction Facilitators"],
  },
  {
    group: "Traders / Buyers",
    icon: "🛒",
    options: ["Wholesalers", "Bulk Traders", "Aggregators"],
  },
  {
    group: "Processors",
    icon: "🏭",
    options: ["Rice Mills", "Flour Mills", "Food Companies"],
  },
  {
    group: "Exporters",
    icon: "🌍",
    options: ["EXIM Traders", "Commodity Exporters"],
  },
  {
    group: "Retailers",
    icon: "🏪",
    options: ["Kirana", "Supermarkets", "Vendors"],
  },
  {
    group: "Authorities",
    icon: "🏛️",
    options: ["APMC Board", "Market Officials"],
  },
  {
    group: "Finance",
    icon: "💳",
    options: ["Banks", "NBFC", "Microfinance"],
  },
  {
    group: "Quality",
    icon: "🧪",
    options: ["Labs", "Agmark"],
  },
  {
    group: "Logistics",
    icon: "🚚",
    options: ["Transport", "Warehouse", "Cold Storage"],
  },
  {
    group: "Digital",
    icon: "💻",
    options: ["e-NAM", "AgriTech"],
  },
  {
    group: "Government",
    icon: "🧾",
    options: ["State Dept", "FCI", "Procurement"],
  },
];

const PROCESS_STEPS = [
  {
    title: "🚜 Farmer Entry",
    buttons: [
      { label: "Add Lot", icon: "➕" },
      { label: "View Lots", icon: "📦" },
      { label: "Dashboard", icon: "📊" },
    ],
  },
  {
    title: "🧪 Quality Check",
    buttons: [
      { label: "Grading", icon: "🏷️" },
      { label: "Lab Test", icon: "🔬" },
      { label: "Quality Reports", icon: "📑" },
    ],
  },
  {
    title: "📢 Auction",
    buttons: [
      { label: "Start Auction", icon: "🚀" },
      { label: "Live Bidding", icon: "⚡" },
      { label: "Bid History", icon: "📜" },
    ],
  },
  {
    title: "💰 Price Finalization",
    buttons: [
      { label: "Weighing", icon: "⚖️" },
      { label: "Generate Slip", icon: "🧾" },
      { label: "Final Price", icon: "💵" },
    ],
  },
  {
    title: "🧾 Payment",
    buttons: [
      { label: "Pay Farmer", icon: "💸" },
      { label: "Transaction Logs", icon: "📚" },
      { label: "Digital Payments", icon: "💳" },
    ],
  },
  {
    title: "🚚 Logistics",
    buttons: [
      { label: "Dispatch", icon: "📤" },
      { label: "Track Delivery", icon: "📍" },
      { label: "Warehouse", icon: "🏬" },
    ],
  },
];

const TOP_APMCS = [
  { name: "Vashi APMC", state: "Maharashtra", commodity: "Fruits & Vegetables" },
  { name: "Lasalgaon APMC", state: "Maharashtra", commodity: "Onion (Asia’s largest)" },
  { name: "Azadpur Mandi", state: "Delhi", commodity: "Fruits & Vegetables (Asia’s largest)" },
  { name: "Koyambedu Market", state: "Tamil Nadu", commodity: "Vegetables" },
  { name: "Unjha APMC", state: "Gujarat", commodity: "Spices (Jeera)" },
  { name: "Indore APMC", state: "Madhya Pradesh", commodity: "Soybean, Grains" },
  { name: "Neemuch APMC", state: "MP", commodity: "Spices, Opium" },
  { name: "Karnal APMC", state: "Haryana", commodity: "Basmati Rice" },
  { name: "Nizamabad APMC", state: "Telangana", commodity: "Turmeric" },
  { name: "Erode Turmeric Market", state: "Tamil Nadu", commodity: "Turmeric" },
  { name: "Sangli APMC", state: "Maharashtra", commodity: "Turmeric, Grapes" },
  { name: "Solapur APMC", state: "Maharashtra", commodity: "Pulses" },
  { name: "Rajkot APMC", state: "Gujarat", commodity: "Groundnut" },
  { name: "Nagpur APMC", state: "Maharashtra", commodity: "Oranges" },
  { name: "Pimpalgaon APMC", state: "Maharashtra", commodity: "Onion" },
  { name: "Chandni Chowk Mandi", state: "Delhi", commodity: "Dry Fruits" },
  { name: "Warangal APMC", state: "Telangana", commodity: "Cotton, Chilli" },
  { name: "Guntur APMC", state: "Andhra Pradesh", commodity: "Chilli" },
  { name: "Hubli APMC", state: "Karnataka", commodity: "Cotton, Groundnut" },
  { name: "Kolhapur APMC", state: "Maharashtra", commodity: "Jaggery" },
];

const STATES = [
  { name: "Maharashtra", hasAPMC: true },
  { name: "Gujarat", hasAPMC: true },
  { name: "Karnataka", hasAPMC: true },
  { name: "Madhya Pradesh", hasAPMC: true },
  { name: "Uttar Pradesh", hasAPMC: true },
  { name: "Rajasthan", hasAPMC: true },
  { name: "Punjab", hasAPMC: true },
  { name: "Haryana", hasAPMC: true },
  { name: "Tamil Nadu", hasAPMC: true },
  { name: "Telangana", hasAPMC: true },
  { name: "Andhra Pradesh", hasAPMC: true },
  { name: "West Bengal", hasAPMC: true },
  { name: "Odisha", hasAPMC: true },
  { name: "Chhattisgarh", hasAPMC: true },

  // ❌ No APMC states
  { name: "Bihar", hasAPMC: false },
  { name: "Kerala", hasAPMC: false },
  { name: "Manipur", hasAPMC: false },
  { name: "Sikkim", hasAPMC: false },
];

const APMC_DATA = {
  Maharashtra: {
    Nashik: ["Lasalgaon", "Pimpalgaon", "Yeola"],
    Pune: ["Pune APMC"],
    Mumbai: ["Vashi APMC"],
    Kolhapur: ["Kolhapur APMC"],
  },
  Gujarat: {
    Mehsana: ["Unjha APMC"],
    Rajkot: ["Rajkot APMC"],
  },
  Karnataka: {
    Hubli: ["Hubli APMC"],
    Mysore: ["Mysore APMC"],
  },
};

export default function APMCHome() {
  const [selectedGroup, setSelectedGroup] = useState("All");
  const [selectedOption, setSelectedOption] = useState("");

  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedAPMC, setSelectedAPMC] = useState("");
  const [selectedTopAPMC, setSelectedTopAPMC] = useState("");

  const stateObj = STATES.find((s) => s.name === selectedState);
  const districts = APMC_DATA[selectedState]
    ? Object.keys(APMC_DATA[selectedState])
    : [];

  const apmcs =
    selectedState && selectedDistrict
        ? APMC_DATA[selectedState]?.[selectedDistrict] || []
        : [];

  const [activeComponent, setActiveComponent] = useState(null);
  const [simulationOn, setSimulationOn] = useState(false);

//   const handleLoadComponent = (label) => {
//     setActiveComponent(label);
//   };

  const handleLoadComponent = (label) => {
    const cleanLabel = label.replace(/^[^\w]+/, "").trim(); // removes emoji
    setActiveComponent(cleanLabel);
  };

  const ActiveComponent = COMPONENT_MAP[activeComponent];

  return (
    <div className="bg-green-600 text-white">
      <div className="mt-1">
        <header className="flex flex-col lg:flex-row lg:justify-between gap-2 mb-2">
            <div className="flex flex-col text-left p-1">
                <h1 className="flex text-left text-lg lg:text-xl font-extrabold text-yellow-200">AgriLink — APMC Digital Platform</h1>
                <p className="text-sm text-white mt-1">Think5Whys APMC Ecosystem: Operate mandi for Farmers, Traders, Agents, Processors & more</p>
            </div>
            <div className="flex flex-row items-center gap-2">
                <label className="text-sm text-white h-10 flex items-center">
                    Stakeholder
                </label>
                <select
                    value={selectedGroup}
                    onChange={(e) => {
                    setSelectedGroup(e.target.value);
                    setSelectedOption("");
                    }}
                    className="px-3 py-2 rounded-lg border bg-white text-black text-sm h-10 min-w-[140px]"
                >
                    <option value="All">All</option>
                    {STAKEHOLDER_GROUPS.map((g) => (
                    <option key={g.group} value={g.group}>
                        {g.icon} {g.group}
                    </option>
                    ))}
                </select>
                <select
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    disabled={selectedGroup === "All"}
                    className="px-3 py-2 rounded-lg border bg-white text-black text-sm h-10 min-w-[140px] disabled:opacity-50"
                >
                    <option value="">Select Type</option>
                    {STAKEHOLDER_GROUPS
                    .find((g) => g.group === selectedGroup)
                    ?.options.map((opt) => (
                        <option key={opt} value={opt}>
                        {opt}
                        </option>
                    ))}
                </select>
                <button
                    onClick={() => {
                    setSelectedGroup("All");
                    setSelectedOption("");
                    }}
                    className="px-3 py-2 bg-white text-black rounded-lg border text-sm h-10"
                >
                    Reset
                </button>
            </div>
        </header>

        {/* ---------------------- PROCESS FLOW ---------------------- */}
        <div className="p-2 bg-green-600 text-white min-h-[70vh]">
            <div className="mb-1 flex flex-wrap items-center gap-2">
                <select
                    value={selectedState}
                    onChange={(e) => {
                    setSelectedState(e.target.value);
                    setSelectedDistrict("");
                    setSelectedAPMC("");
                    }}
                    className="px-2 py-1 rounded bg-white text-black"
                >
                    <option value="">Select State</option>
                    {STATES.map((s) => (
                    <option key={s.name} value={s.name}>
                        {s.name} {!s.hasAPMC && "(No APMC)"}
                    </option>
                    ))}
                </select>
                <select
                    value={selectedDistrict}
                    onChange={(e) => {
                    setSelectedDistrict(e.target.value);
                    setSelectedAPMC("");
                    }}
                    disabled={!stateObj?.hasAPMC}
                    className="px-2 py-1 rounded bg-white text-black disabled:opacity-50"
                >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                    <option key={d} value={d}>
                        {d}
                    </option>
                    ))}
                </select>
                <select
                    value={selectedAPMC}
                    onChange={(e) => setSelectedAPMC(e.target.value)}
                    disabled={!stateObj?.hasAPMC}
                    className="px-2 py-1 rounded bg-white text-black disabled:opacity-50"
                >
                    <option value="">Select APMC</option>
                    {apmcs.map((a) => (
                    <option key={a} value={a}>
                        {a}
                    </option>
                    ))}
                </select>
                <select
                    value={selectedTopAPMC}
                    onChange={(e) => setSelectedTopAPMC(e.target.value)}
                    className="px-2 py-1 rounded bg-yellow-200 text-black"
                >
                    <option value="">🌟 Top APMCs</option>
                    {TOP_APMCS.map((t) => (
                    <option key={t.name} value={t.name}>
                        {t.name} ({t.commodity})
                    </option>
                    ))}
                </select>
            </div>
            <div className="mb-1 flex items-center gap-2">
                <input
                type="checkbox"
                checked={simulationOn}
                onChange={(e) => setSimulationOn(e.target.checked)}
                />
                <label>Load Sample Process Flow Simulation</label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
                {PROCESS_STEPS.map((step, index) => (
                    <div key={index} className="bg-gray-900 rounded-2xl p-2 border">
                        <h3 className="text-cyan-200 font-semibold mb-1">
                        {step.title}
                        </h3>

                        <div className="flex flex-row gap-1">
                            {step.buttons.map((btn) => (
                                <button
                                    key={btn.label}
                                    onClick={() => handleLoadComponent(btn.label)}
                                    className="flex-1 min-w-[90px] px-1 py-2 bg-blue-600 hover:bg-blue-500 active:scale-95 transition rounded-xl text-xs font-medium shadow"
                                >
                                    {btn.icon} {btn.label}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {ActiveComponent && (
                <div className="mt-2 bg-black [&_p]:text-white text-white [&_li]:text-gray-100 p-2 rounded-xl">
                    <ActiveComponent data={simulationOn ? SAMPLE_DATA : null} />
                </div>
            )}
         </div>
      </div>

      <footer className="bg-black text-white py-2 px-6 mt-2">
        <div className="bg-blcak flex flex-col lg:flex-row justify-between">
          <p className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions</p>
          <div className="flex space-x-6 mt-4 lg:mt-0 text-sm text-white">
            <Link className="text-white" to="/about-us">About</Link>
            <Link className="text-white" to="/privacy-policy">Privacy</Link>
            <Link className="text-white" to="/terms-of-use">Terms</Link>
            <Link className="text-white" to="/contact-us">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}